import React, { useState, useEffect, useMemo } from 'react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { useAuth } from '../../hooks/useAuth';
import {
  Trash2,
  Users,
  DollarSign,
  AlertCircle,
  Save,
  Loader2,
  Search,
  RefreshCw,
  Calendar,
  TrendingUp,
  TrendingDown,
  Download,
  Bell,
  Activity,
  UserCheck,
  UserX,
  CreditCard,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart as RPieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import { format, subMonths, startOfMonth, endOfMonth, parseISO, subDays, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Types
type ProfileFromDB = {
  id: string;
  email: string | null;
  nome: string;
  nome_completo: string | null;
  created_at: string | null;
  plano_atual: string | null;
  plano_expira_em: string | null;
  trial_ends_at: string | null;
  lifetime: boolean | null;
  role: string | null;
};

interface StripeSubscription {
  subscription_id: string;
  user_id: string;
  nome: string;
  email: string;
  plan_type: 'monthly' | 'annual';
  price_id: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
}

interface RevenueData {
  month: string;
  revenue: number;
  subscriptions: number;
  newSubs: number;
  churnedSubs: number;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  trend?: 'up' | 'down' | 'neutral';
}

// Pricing constants
const PRICING = {
  monthly: 27.00,
  annual: 270.00,
  annualMonthly: 22.50 // Annual divided by 12
};

// Color constants (no purple/indigo)
const COLORS = {
  primary: '#00FF7F',
  green: '#10B981',
  blue: '#3B82F6',
  orange: '#F97316',
  amber: '#F59E0B',
  red: '#EF4444',
  teal: '#14B8A6',
  cyan: '#06B6D4',
  gray: '#6B7280'
};

const CHART_COLORS = {
  revenue: COLORS.green,
  monthly: COLORS.blue,
  annual: COLORS.orange,
  trial: COLORS.amber,
  active: COLORS.green,
  canceled: COLORS.red,
  pastDue: COLORS.orange,
  newUsers: COLORS.teal
};

function determinePlanType(profile: ProfileFromDB): string {
  if (profile.lifetime) return 'lifetime';
  if (profile.trial_ends_at && new Date(profile.trial_ends_at) > new Date()) return 'trial';
  if (!profile.plano_atual) return 'free';
  return 'monthly';
}

function determinePlanName(profile: ProfileFromDB): string {
  if (profile.lifetime) return 'Lifetime';
  if (profile.trial_ends_at && new Date(profile.trial_ends_at) > new Date()) return 'Trial';
  if (profile.plano_atual) return profile.plano_atual;
  return 'Gratuito';
}

function deriveUserStatus(profile: ProfileFromDB): boolean {
  if (profile.lifetime) return true;
  if (profile.trial_ends_at) {
    const trialEnd = new Date(profile.trial_ends_at);
    if (trialEnd > new Date()) return true;
  }
  if (profile.plano_expira_em) {
    const planEnd = new Date(profile.plano_expira_em);
    if (planEnd > new Date()) return true;
  }
  return false;
}

function getExpirationDate(profile: ProfileFromDB): string | undefined {
  if (profile.lifetime) return undefined;
  if (profile.plano_expira_em) return profile.plano_expira_em;
  if (profile.trial_ends_at) return profile.trial_ends_at;
  return undefined;
}

type UserProfile = {
  id: string;
  email: string;
  nome: string;
  nome_completo?: string;
  created_at?: string;
  plan_type?: string;
  plan_name?: string;
  expires_at?: string;
  is_active?: boolean;
};

// Metric Card Component
const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  changeLabel,
  icon,
  iconBg,
  iconColor,
  trend
}) => (
  <div className="card p-6">
    <div className="flex items-start justify-between mb-4">
      <div className="flex-1">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{title}</p>
        <p className="text-3xl font-bold text-gray-900 dark:text-white">{value}</p>
      </div>
      <div className={`p-3 rounded-lg ${iconBg}`}>
        <div className={iconColor}>
          {icon}
        </div>
      </div>
    </div>
    {change !== undefined && (
      <div className="flex items-center gap-2">
        {trend === 'up' && <ArrowUpRight className="w-4 h-4 text-green-500" />}
        {trend === 'down' && <ArrowDownRight className="w-4 h-4 text-red-500" />}
        <span className={`text-sm font-medium ${
          trend === 'up' ? 'text-green-600 dark:text-green-400' :
          trend === 'down' ? 'text-red-600 dark:text-red-400' :
          'text-gray-600 dark:text-gray-400'
        }`}>
          {change > 0 ? '+' : ''}{change}%
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-400">{changeLabel}</span>
      </div>
    )}
  </div>
);

export default function AdminDashboardPage() {
  const { supabase } = useSupabase();
  const { user } = useAuth();

  // State
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [stripeSubscriptions, setStripeSubscriptions] = useState<StripeSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingUser, setUpdatingUser] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<string | null>(null);
  const [exportingData, setExportingData] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'trial' | 'expired' | 'free'>('all');
  const [timeRange, setTimeRange] = useState<'7days' | '30days' | '90days' | '6months'>('30days');
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);

  // Plan modal
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [newPlanType, setNewPlanType] = useState<string>('');
  const [durationDays, setDurationDays] = useState<number>(30);

  // Fetch all data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch users from profiles
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('id, email, nome, nome_completo, created_at, plano_atual, plano_expira_em, trial_ends_at, lifetime, role')
        .order('created_at', { ascending: false });

      if (userError) throw userError;

      const formattedUsers = (userData || []).map((user: ProfileFromDB) => ({
        id: user.id,
        email: user.email || 'N/A',
        nome: user.nome || user.nome_completo || 'N/A',
        nome_completo: user.nome_completo,
        created_at: user.created_at,
        plan_type: determinePlanType(user),
        plan_name: determinePlanName(user),
        expires_at: getExpirationDate(user),
        is_active: deriveUserStatus(user),
      }));

      setUsers(formattedUsers);

      // Fetch Stripe subscriptions
      await fetchStripeSubscriptions();

      // Fetch revenue data
      await fetchRevenueData();

    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError(error.message || 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeSubscriptions = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-get-subscriptions`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch subscriptions');

      const result = await response.json();
      if (result.success && result.subscriptions) {
        setStripeSubscriptions(result.subscriptions);
      }
    } catch (error) {
      console.error('Error fetching Stripe subscriptions:', error);
    }
  };

  const fetchRevenueData = async () => {
    try {
      const months: RevenueData[] = [];

      for (let i = 5; i >= 0; i--) {
        const date = subMonths(new Date(), i);
        const monthName = format(date, 'MMM yyyy', { locale: ptBR });
        const startDate = startOfMonth(date);
        const endDate = endOfMonth(date);

        // Get subscriptions active during this month
        const { data: monthSubs } = await supabase
          .from('stripe_subscriptions')
          .select('*')
          .lte('current_period_start', Math.floor(endDate.getTime() / 1000))
          .gte('current_period_end', Math.floor(startDate.getTime() / 1000))
          .in('status', ['active', 'trialing']);

        const monthlyCount = monthSubs?.filter(s => s.price_id.includes('month') ||
          s.price_id === 'price_1RhgRDB0B3nriuwY73GigtEm' ||
          s.price_id === 'price_1RIKr4B0B3nriuwYoFRXi3rH').length || 0;
        const annualCount = monthSubs?.filter(s => !s.price_id.includes('month') &&
          s.price_id !== 'price_1RhgRDB0B3nriuwY73GigtEm' &&
          s.price_id !== 'price_1RIKr4B0B3nriuwYoFRXi3rH').length || 0;

        const revenue = (monthlyCount * PRICING.monthly) + (annualCount * PRICING.annualMonthly);

        // Get new subscriptions this month
        const { count: newSubsCount } = await supabase
          .from('stripe_subscriptions')
          .select('*', { count: 'exact', head: true })
          .gte('current_period_start', Math.floor(startDate.getTime() / 1000))
          .lte('current_period_start', Math.floor(endDate.getTime() / 1000));

        // Get churned subscriptions
        const { count: churnedCount } = await supabase
          .from('stripe_subscriptions')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'canceled')
          .gte('canceled_at', Math.floor(startDate.getTime() / 1000))
          .lte('canceled_at', Math.floor(endDate.getTime() / 1000));

        months.push({
          month: monthName,
          revenue: Math.round(revenue * 100) / 100,
          subscriptions: monthSubs?.length || 0,
          newSubs: newSubsCount || 0,
          churnedSubs: churnedCount || 0
        });
      }

      setRevenueData(months);
    } catch (error) {
      console.error('Error fetching revenue data:', error);
    }
  };

  useEffect(() => {
    fetchData();

    // Subscribe to realtime changes
    const channel = supabase
      .channel('admin_dashboard')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => fetchData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'stripe_subscriptions' }, () => fetchData())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabase, timeRange]);

  // Calculate metrics
  const metrics = useMemo(() => {
    const activeStripeSubs = stripeSubscriptions.filter(s =>
      s.status === 'active' || s.status === 'trialing'
    );

    const monthlySubs = activeStripeSubs.filter(s => s.plan_type === 'monthly');
    const annualSubs = activeStripeSubs.filter(s => s.plan_type === 'annual');

    const mrr = (monthlySubs.length * PRICING.monthly) + (annualSubs.length * PRICING.annualMonthly);
    const arr = mrr * 12;

    const activePaidUsers = activeStripeSubs.length;
    const trialUsers = users.filter(u => u.plan_type === 'trial').length;
    const freeUsers = users.filter(u => u.plan_type === 'free').length;
    const lifetimeUsers = users.filter(u => u.plan_type === 'lifetime').length;

    // Calculate churn rate (last 30 days)
    const thirtyDaysAgo = Math.floor(subDays(new Date(), 30).getTime() / 1000);
    const canceledLast30Days = stripeSubscriptions.filter(s =>
      s.status === 'canceled' &&
      s.current_period_end &&
      parseInt(s.current_period_end) >= thirtyDaysAgo
    ).length;

    const activeAtStartOf30Days = activePaidUsers + canceledLast30Days;
    const churnRate = activeAtStartOf30Days > 0
      ? (canceledLast30Days / activeAtStartOf30Days) * 100
      : 0;

    // ARPU
    const arpu = activePaidUsers > 0 ? mrr / activePaidUsers : 0;

    // LTV estimate (ARPU * average lifetime in months, assume 24 months)
    const ltv = arpu * 24;

    // Calculate growth
    const currentMonth = revenueData[revenueData.length - 1];
    const lastMonth = revenueData[revenueData.length - 2];
    const mrrGrowth = lastMonth && lastMonth.revenue > 0
      ? ((currentMonth?.revenue - lastMonth.revenue) / lastMonth.revenue) * 100
      : 0;

    const subsGrowth = lastMonth && lastMonth.subscriptions > 0
      ? ((currentMonth?.subscriptions - lastMonth.subscriptions) / lastMonth.subscriptions) * 100
      : 0;

    // Past due subscriptions
    const pastDueSubs = stripeSubscriptions.filter(s => s.status === 'past_due').length;

    // Expiring soon (next 7 days)
    const sevenDaysFromNow = Math.floor(subDays(new Date(), -7).getTime() / 1000);
    const expiringSoon = activeStripeSubs.filter(s =>
      s.current_period_end &&
      parseInt(s.current_period_end) <= sevenDaysFromNow &&
      parseInt(s.current_period_end) >= Math.floor(new Date().getTime() / 1000)
    ).length;

    return {
      mrr,
      arr,
      activePaidUsers,
      trialUsers,
      freeUsers,
      lifetimeUsers,
      churnRate,
      arpu,
      ltv,
      mrrGrowth,
      subsGrowth,
      pastDueSubs,
      expiringSoon,
      totalUsers: users.length,
      conversionRate: users.length > 0 ? (activePaidUsers / users.length) * 100 : 0
    };
  }, [stripeSubscriptions, users, revenueData]);

  // Subscription status distribution
  const statusDistribution = useMemo(() => {
    const dist: { [key: string]: number } = {};
    stripeSubscriptions.forEach(sub => {
      dist[sub.status] = (dist[sub.status] || 0) + 1;
    });

    return Object.entries(dist).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
      color: name === 'active' ? CHART_COLORS.active :
             name === 'trialing' ? CHART_COLORS.trial :
             name === 'canceled' ? CHART_COLORS.canceled :
             name === 'past_due' ? CHART_COLORS.pastDue :
             COLORS.gray
    }));
  }, [stripeSubscriptions]);

  // Plan distribution
  const planDistribution = useMemo(() => {
    const monthly = stripeSubscriptions.filter(s =>
      (s.status === 'active' || s.status === 'trialing') && s.plan_type === 'monthly'
    ).length;
    const annual = stripeSubscriptions.filter(s =>
      (s.status === 'active' || s.status === 'trialing') && s.plan_type === 'annual'
    ).length;

    return [
      { name: 'Mensal', value: monthly, color: CHART_COLORS.monthly },
      { name: 'Anual', value: annual, color: CHART_COLORS.annual },
      { name: 'Trial', value: metrics.trialUsers, color: CHART_COLORS.trial },
      { name: 'Gratuito', value: metrics.freeUsers, color: COLORS.gray },
      { name: 'Lifetime', value: metrics.lifetimeUsers, color: COLORS.teal }
    ].filter(item => item.value > 0);
  }, [stripeSubscriptions, metrics]);

  const handleUpdatePlan = async (userId: string, planType: string, days: number = 30) => {
    setUpdatingUser(userId);
    try {
      const updates: any = {};

      if (planType === 'free') {
        updates.plano_atual = null;
        updates.plano_expira_em = null;
        updates.trial_ends_at = null;
        updates.lifetime = false;
      } else if (planType === 'trial') {
        const trialEnd = new Date();
        trialEnd.setDate(trialEnd.getDate() + days);
        updates.trial_ends_at = trialEnd.toISOString();
        updates.plano_atual = null;
        updates.plano_expira_em = null;
        updates.lifetime = false;
      } else if (planType === 'lifetime') {
        updates.lifetime = true;
        updates.trial_ends_at = null;
        updates.plano_atual = 'Premium';
        updates.plano_expira_em = null;
      } else {
        const endDate = new Date();
        if (planType === 'monthly') {
          endDate.setMonth(endDate.getMonth() + 1);
          updates.plano_atual = 'Premium';
        } else if (planType === 'annual') {
          endDate.setFullYear(endDate.getFullYear() + 1);
          updates.plano_atual = 'Premium Anual';
        }
        updates.plano_expira_em = endDate.toISOString();
        updates.trial_ends_at = null;
        updates.lifetime = false;
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId);

      if (error) throw error;
      await fetchData();
    } catch (error: any) {
      console.error('Error updating user plan:', error);
      setError('Error updating user plan: ' + error.message);
    } finally {
      setUpdatingUser(null);
      setShowPlanModal(false);
    }
  };

  const handleDelete = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

    setDeletingUser(userId);
    try {
      const { error } = await supabase.rpc('admin_delete_user', { uid: userId });
      if (error) throw error;
      await fetchData();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setError('Error deleting user: ' + error.message);
    } finally {
      setDeletingUser(null);
    }
  };

  const handleExportData = async () => {
    setExportingData(true);
    try {
      const doc = new jsPDF();

      doc.setFontSize(18);
      doc.text('Presida.Club - Admin Report', 14, 22);
      doc.setFontSize(12);
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 30);

      doc.setFontSize(14);
      doc.text('Financial Metrics', 14, 45);

      const financialStats = [
        ['MRR (Monthly Recurring Revenue)', `R$ ${metrics.mrr.toFixed(2)}`],
        ['ARR (Annual Recurring Revenue)', `R$ ${metrics.arr.toFixed(2)}`],
        ['ARPU (Average Revenue Per User)', `R$ ${metrics.arpu.toFixed(2)}`],
        ['LTV (Lifetime Value)', `R$ ${metrics.ltv.toFixed(2)}`],
        ['Churn Rate (30 days)', `${metrics.churnRate.toFixed(2)}%`]
      ];

      (doc as any).autoTable({
        startY: 50,
        head: [['Metric', 'Value']],
        body: financialStats,
        theme: 'grid',
        headStyles: { fillColor: [16, 185, 129] }
      });

      doc.setFontSize(14);
      doc.text('Subscription Statistics', 14, (doc as any).lastAutoTable.finalY + 15);

      const subStats = [
        ['Active Paid Subscribers', metrics.activePaidUsers.toString()],
        ['Trial Users', metrics.trialUsers.toString()],
        ['Free Users', metrics.freeUsers.toString()],
        ['Lifetime Users', metrics.lifetimeUsers.toString()],
        ['Total Users', metrics.totalUsers.toString()],
        ['Conversion Rate', `${metrics.conversionRate.toFixed(2)}%`]
      ];

      (doc as any).autoTable({
        startY: (doc as any).lastAutoTable.finalY + 20,
        head: [['Metric', 'Value']],
        body: subStats,
        theme: 'grid',
        headStyles: { fillColor: [16, 185, 129] }
      });

      doc.save('presida-admin-report.pdf');
    } catch (error) {
      console.error('Error exporting data:', error);
      setError('Error exporting data');
    } finally {
      setExportingData(false);
    }
  };

  const openPlanModal = (user: UserProfile) => {
    setSelectedUser(user);
    setNewPlanType(user.plan_type || 'free');
    setShowPlanModal(true);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.nome_completo?.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesStatus = true;
    if (statusFilter !== 'all') {
      switch (statusFilter) {
        case 'active':
          matchesStatus = user.is_active && user.plan_type !== 'trial' && user.plan_type !== 'free';
          break;
        case 'trial':
          matchesStatus = user.plan_type === 'trial';
          break;
        case 'free':
          matchesStatus = user.plan_type === 'free';
          break;
        case 'expired':
          matchesStatus = !user.is_active && user.plan_type !== 'free';
          break;
      }
    }

    return matchesSearch && matchesStatus;
  });

  const getPlanBadgeColor = (planType?: string) => {
    switch (planType) {
      case 'monthly':
        return 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400';
      case 'annual':
        return 'bg-orange-100 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400';
      case 'trial':
        return 'bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400';
      case 'lifetime':
        return 'bg-teal-100 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400';
      default:
        return 'bg-gray-100 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <header className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Admin Dashboard</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Visão completa de receitas, assinaturas e usuários da plataforma
        </p>
      </header>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}

      {/* Primary Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="MRR (Receita Mensal Recorrente)"
          value={`R$ ${metrics.mrr.toFixed(2)}`}
          change={metrics.mrrGrowth}
          changeLabel="vs. mês anterior"
          icon={<DollarSign className="w-6 h-6" />}
          iconBg="bg-green-100 dark:bg-green-900/20"
          iconColor="text-green-600 dark:text-green-400"
          trend={metrics.mrrGrowth > 0 ? 'up' : metrics.mrrGrowth < 0 ? 'down' : 'neutral'}
        />

        <MetricCard
          title="ARR (Receita Anual Recorrente)"
          value={`R$ ${metrics.arr.toFixed(2)}`}
          icon={<TrendingUp className="w-6 h-6" />}
          iconBg="bg-blue-100 dark:bg-blue-900/20"
          iconColor="text-blue-600 dark:text-blue-400"
        />

        <MetricCard
          title="Assinantes Pagos Ativos"
          value={metrics.activePaidUsers}
          change={metrics.subsGrowth}
          changeLabel="vs. mês anterior"
          icon={<UserCheck className="w-6 h-6" />}
          iconBg="bg-teal-100 dark:bg-teal-900/20"
          iconColor="text-teal-600 dark:text-teal-400"
          trend={metrics.subsGrowth > 0 ? 'up' : metrics.subsGrowth < 0 ? 'down' : 'neutral'}
        />

        <MetricCard
          title="Taxa de Conversão"
          value={`${metrics.conversionRate.toFixed(1)}%`}
          icon={<Activity className="w-6 h-6" />}
          iconBg="bg-orange-100 dark:bg-orange-900/20"
          iconColor="text-orange-600 dark:text-orange-400"
        />
      </div>

      {/* Secondary Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="ARPU (Receita Média por Usuário)"
          value={`R$ ${metrics.arpu.toFixed(2)}`}
          icon={<BarChart3 className="w-6 h-6" />}
          iconBg="bg-cyan-100 dark:bg-cyan-900/20"
          iconColor="text-cyan-600 dark:text-cyan-400"
        />

        <MetricCard
          title="Taxa de Churn (30 dias)"
          value={`${metrics.churnRate.toFixed(1)}%`}
          icon={<UserX className="w-6 h-6" />}
          iconBg="bg-red-100 dark:bg-red-900/20"
          iconColor="text-red-600 dark:text-red-400"
          trend={metrics.churnRate > 5 ? 'down' : 'neutral'}
        />

        <MetricCard
          title="Usuários em Trial"
          value={metrics.trialUsers}
          changeLabel={`Meta: ${Math.round(metrics.trialUsers * 0.3)} conversões`}
          icon={<Clock className="w-6 h-6" />}
          iconBg="bg-amber-100 dark:bg-amber-900/20"
          iconColor="text-amber-600 dark:text-amber-400"
        />

        <MetricCard
          title="LTV Estimado"
          value={`R$ ${metrics.ltv.toFixed(2)}`}
          icon={<TrendingUp className="w-6 h-6" />}
          iconBg="bg-green-100 dark:bg-green-900/20"
          iconColor="text-green-600 dark:text-green-400"
        />
      </div>

      {/* Alerts and Action Items */}
      {(metrics.pastDueSubs > 0 || metrics.expiringSoon > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {metrics.pastDueSubs > 0 && (
            <div className="card p-6 border-l-4 border-red-500">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-500 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-1">Pagamentos Vencidos</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-2">
                    {metrics.pastDueSubs} assinatura{metrics.pastDueSubs > 1 ? 's' : ''} com pagamento vencido
                  </p>
                  <button className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 font-medium">
                    Ver detalhes →
                  </button>
                </div>
              </div>
            </div>
          )}

          {metrics.expiringSoon > 0 && (
            <div className="card p-6 border-l-4 border-amber-500">
              <div className="flex items-start gap-3">
                <Clock className="w-6 h-6 text-amber-500 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-1">Expirando em Breve</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-2">
                    {metrics.expiringSoon} assinatura{metrics.expiringSoon > 1 ? 's' : ''} expirando nos próximos 7 dias
                  </p>
                  <button className="text-sm text-amber-600 hover:text-amber-700 dark:text-amber-400 font-medium">
                    Ver detalhes →
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Revenue Trend Chart */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold">Evolução da Receita (MRR)</h2>
            <button
              onClick={fetchRevenueData}
              className="p-2 hover:bg-gray-100 dark:hover:bg-dark-light rounded-full transition-colors"
              title="Atualizar dados"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.revenue} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={CHART_COLORS.revenue} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip
                  formatter={(value: any) => [`R$ ${value.toFixed(2)}`, 'Receita']}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke={CHART_COLORS.revenue}
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Subscription Growth Chart */}
        <div className="card p-6">
          <h2 className="text-lg font-bold mb-4">Crescimento de Assinaturas</h2>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="newSubs" name="Novas" fill={CHART_COLORS.active} />
                <Bar dataKey="churnedSubs" name="Canceladas" fill={CHART_COLORS.canceled} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Distribution Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Subscription Status Distribution */}
        <div className="card p-6">
          <h2 className="text-lg font-bold mb-4">Status das Assinaturas</h2>
          <div className="h-[300px] flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <RPieChart>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </RPieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {statusDistribution.map((item) => (
              <div key={item.name} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                <span className="text-sm">{item.name}: {item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Plan Distribution */}
        <div className="card p-6">
          <h2 className="text-lg font-bold mb-4">Distribuição por Plano</h2>
          <div className="h-[300px] flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <RPieChart>
                <Pie
                  data={planDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {planDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </RPieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {planDistribution.map((plan) => (
              <div key={plan.name} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: plan.color }}></div>
                <span className="text-sm">{plan.name}: {plan.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6 mb-8">
        <h2 className="text-lg font-bold mb-4">Ações Rápidas</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={handleExportData}
            className="btn-primary flex items-center justify-center gap-2"
            disabled={exportingData}
          >
            {exportingData ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Exportando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Exportar Relatório PDF
              </>
            )}
          </button>

          <button
            onClick={fetchData}
            className="btn-outline flex items-center justify-center gap-2"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar Dados
          </button>

          <button className="btn-outline flex items-center justify-center gap-2">
            <Bell className="w-4 h-4" />
            Enviar Notificação
          </button>
        </div>
      </div>

      {/* User Management Table */}
      <div className="card overflow-hidden">
        <div className="p-6 border-b dark:border-dark-border">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold">Gerenciamento de Usuários</h2>
              <span className="text-sm text-gray-500">
                ({filteredUsers.length} usuários)
              </span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar usuários..."
                  className="input pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <select
                className="input"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
              >
                <option value="all">Todos</option>
                <option value="active">Pagos Ativos</option>
                <option value="trial">Trial</option>
                <option value="free">Gratuitos</option>
                <option value="expired">Expirados</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-dark-light">
                <th className="text-left p-4 font-medium">Usuário</th>
                <th className="text-left p-4 font-medium">Plano</th>
                <th className="text-left p-4 font-medium">Status</th>
                <th className="text-left p-4 font-medium">Expira</th>
                <th className="text-left p-4 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-dark-border">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-dark-light">
                  <td className="p-4">
                    <div>
                      <p className="font-medium">{user.nome || user.email}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      <p className="text-xs text-gray-400">
                        Cadastro: {formatDate(user.created_at)}
                      </p>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      getPlanBadgeColor(user.plan_type)
                    }`}>
                      {user.plan_name || 'Unknown'}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      user.is_active
                        ? 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                        : 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                    }`}>
                      {user.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="p-4">
                    {user.plan_type === 'lifetime' ? (
                      <span className="text-teal-600 dark:text-teal-400">Vitalício</span>
                    ) : user.expires_at ? (
                      formatDate(user.expires_at)
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openPlanModal(user)}
                        className="btn-primary text-sm py-1 px-3"
                        disabled={updatingUser === user.id}
                      >
                        {updatingUser === user.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Atualizar'
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        disabled={deletingUser === user.id || user.email === 'rica@presida.club'}
                        className={`text-red-500 hover:text-red-700 p-2 ${
                          user.email === 'rica@presida.club'
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                        title={user.email === 'rica@presida.club'
                          ? 'Cannot delete admin user'
                          : 'Delete user'}
                      >
                        {deletingUser === user.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Trash2 className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Plan Update Modal */}
      {showPlanModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-dark-paper rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b dark:border-dark-border">
              <h2 className="text-xl font-bold">Atualizar Plano do Usuário</h2>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-2">Usuário:</p>
                <div className="p-3 bg-gray-50 dark:bg-dark-light rounded-lg">
                  <p className="font-medium">{selectedUser.nome || selectedUser.email}</p>
                  <p className="text-sm text-gray-500">{selectedUser.email}</p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-2">Plano Atual:</p>
                <div className="p-3 bg-gray-50 dark:bg-dark-light rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      getPlanBadgeColor(selectedUser.plan_type)
                    }`}>
                      {selectedUser.plan_name || 'Unknown'}
                    </span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      selectedUser.is_active
                        ? 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                        : 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                    }`}>
                      {selectedUser.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  {selectedUser.expires_at && selectedUser.plan_type !== 'lifetime' && (
                    <p className="text-sm mt-2">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Expira: {formatDate(selectedUser.expires_at)}
                    </p>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Novo Plano:
                </label>
                <select
                  className="input w-full"
                  value={newPlanType}
                  onChange={(e) => setNewPlanType(e.target.value)}
                >
                  <option value="free">Gratuito</option>
                  <option value="trial">Trial</option>
                  <option value="monthly">Mensal</option>
                  <option value="annual">Anual</option>
                  <option value="lifetime">Vitalício</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  Duração (dias):
                </label>
                <input
                  type="number"
                  className="input w-full"
                  value={durationDays}
                  onChange={(e) => setDurationDays(parseInt(e.target.value))}
                  min={1}
                  max={365}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Aplica-se apenas a Trial e planos pagos. Vitalício e Gratuito ignoram este valor.
                </p>
              </div>

              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowPlanModal(false)}
                  className="btn-outline"
                  disabled={!!updatingUser}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleUpdatePlan(selectedUser.id, newPlanType, durationDays)}
                  className="btn-primary"
                  disabled={!!updatingUser}
                >
                  {updatingUser ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Atualizando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Atualizar Plano
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
